import api from './api';

export interface CourseInfo {
    id: number;
    title: string;
    available?: boolean;
}

export interface LessonInfo {
    id: number;
    title: string;
}

export interface Note {
    id: number;
    title: string | null;
    content: string;
    color: string | null;
    isPrivate: boolean;
    createdAt: string;
    updatedAt: string;
    course: CourseInfo | null;
    lesson: LessonInfo | null;
}

export interface NotesListResponse {
    notes: Note[];
    total: number;
}

export interface NotesByCourseResponse extends NotesListResponse {
    course: CourseInfo;
}

export interface NotesByLessonResponse extends NotesListResponse {
    lesson: LessonInfo;
    course: CourseInfo | null;
}

export interface CreateNoteData {
    courseId: number;
    lessonId?: number;
    title?: string;
    content: string;
    color?: string;
    isPrivate?: boolean;
}

export interface UpdateNoteData {
    title?: string;
    content?: string;
    color?: string;
    isPrivate?: boolean;
}

class NoteService {
    /**
     * Get all notes for the current user
     */
    async getNotes(): Promise<Note[]> {
        const response = await api.get<NotesListResponse>('/notes');
        return response.data.notes;
    }

    /**
     * Get notes for a specific course
     */
    async getNotesByCourse(courseId: number): Promise<Note[]> {
        const response = await api.get<NotesByCourseResponse>(`/notes/course/${courseId}`);
        return response.data.notes;
    }

    /**
     * Get notes for a specific lesson
     */
    async getNotesByLesson(lessonId: number): Promise<Note[]> {
        const response = await api.get<NotesByLessonResponse>(`/notes/lesson/${lessonId}`);
        return response.data.notes;
    }

    /**
     * Create a new note
     */
    async createNote(data: CreateNoteData): Promise<Note> {
        const response = await api.post<Note>('/notes', data);
        return response.data;
    }

    /**
     * Update an existing note
     */
    async updateNote(id: number, data: UpdateNoteData): Promise<Note> {
        const response = await api.patch<Note>(`/notes/${id}`, data);
        return response.data;
    }

    /**
     * Delete a note
     */
    async deleteNote(id: number): Promise<void> {
        await api.delete(`/notes/${id}`);
    }

    /**
     * Get note count for user
     */
    async getNoteCount(): Promise<number> {
        const response = await api.get<{ count: number }>('/notes/count');
        return response.data.count;
    }
}

export const noteService = new NoteService();
export default noteService;
